<template>
  <transition name="modal">
    <div class="modal-mask">
      <div class="modal-wrapper" id="wrapper">
        <transition name="expand">
          <!-- When revocations are disabled -->
          <div v-if="!isRevocationsAllowed">
            <not-allowed-view @close-modal="closeModal" />
          </div>
          <div v-else-if="hasDeadlinePassed">
            <!-- When deadline to revoke proxies is passed -->
            <deadline-passed-view
              :deadline-formatted="deadlineFormatted"
              @close-modal="closeModal"
            />
          </div>
          <div v-else-if="isLoading">
            <!-- When revocation is in progress -->
            <div class="mdl-dialog modal-container center">
              <div class="mdl-dialog__content">
                <h4>Loading, please wait...</h4>
                <p class="mdl-spinner mdl-js-spinner is-active"></p>
              </div>
            </div>
          </div>
          <div v-else-if="!isLoading && hasError">
            <!-- When there is an error with the revocation request -->
            <response-error-view :error="error" @close-modal="closeModal" />
          </div>
          <div v-else-if="!isLoading && isCompleted && !hasError">
            <!-- When revocation is successfull -->
            <success-view :response="response" @close-modal="closeModal" />
          </div>
          <div v-else-if="hasVoter">
            <!-- This will be the default prompt -->
            <default-view
              :unit="unit"
              :address="address"
              @revoke="handleClick"
              @close-modal="closeModal"
            />
          </div>
          <div v-else>
            <!-- For when there's an error with the voter's token -->
            <token-error-view @close-modal="closeModal" />
          </div>
        </transition>
      </div>
    </div>
  </transition>
</template>

<script>
import moment from 'moment';
import {mapGetters, mapActions} from 'vuex';

import {formatDate} from '@/vuex/helpers';

import DeadlinePassedView from './RevokeModalDeadline';
import NotAllowedView from './RevokeModalNotAllowed';
import DefaultView from './RevokeModalDefault';
import TokenErrorView from './RevokeModalTokenError';
import SuccessView from './RevokeModalSuccess';
import ResponseErrorView from './RevokeModalResponseError';

export default {
  name: 'RevocationModal',
  props: {
    unit: {
      type: String,
      required: true
    },
    address: {
      type: String,
      required: true
    },
    revocationDeadline: {
      type: [String, Date],
      default() {
        return null;
      }
    },
    timezone: {
      type: String,
      default: null
    }
  },
  components: {
    DefaultView,
    TokenErrorView,
    ResponseErrorView,
    NotAllowedView,
    DeadlinePassedView,
    SuccessView
  },
  data() {
    return {
      isLoading: false,
      isCompleted: false,
      error: {},
      response: {
        status: null,
        statusText: null,
        data: {}
      }
    };
  },
  computed: {
    ...mapGetters(['token', 'shortCode']),
    hasVoter() {
      return !!this.unit;
    },
    isRevocationsAllowed() {
      return this.revocationDeadline;
    },
    hasDeadlinePassed() {
      if (!this.isRevocationsAllowed) {
        return false;
      }
      return moment().isSameOrAfter(this.revocationDeadline);
    },
    deadlineFormatted() {
      if (this.revocationDeadline) {
        return formatDate(
          this.revocationDeadline,
          this.timezone,
          'MMM DD YYYY hh:mma zz'
        );
      }
    },
    currentTime() {
      return formatDate(new Date(), this.timezone, 'MMM DD YYYY hh:mma zz');
    },
    hasError() {
      return Object.keys(this.error).length > 0;
    }
  },
  methods: {
    ...mapActions('voter', ['revokeSubmissions']),
    // close modal if background is clicked
    closeModal() {
      this.$emit('close-modal');
    },
    async handleClick() {
      try {
        this.error = {};
        this.isCompleted = false;
        this.isLoading = true;
        // Set timeout hack to activate the spinner animation
        setTimeout(function() {
          componentHandler.upgradeAllRegistered();
        }, 50);

        const results = await this.revokeSubmissions({
          shortCode: this.shortCode,
          token: this.token
        });

        this.response = results;
        this.isCompleted = true;
        this.error = {};
      } catch (err) {
        console.error(err);
        this.error = err;
      } finally {
        const self = this;
        // Give it a bit of an animation
        setTimeout(function() {
          self.isLoading = false;
        }, 500);
      }
    }
  },
  mounted() {
    componentHandler.upgradeAllRegistered();
  }
};
</script>

<style>
.big-icon {
  font-size: 102px;
}

@media only screen and (max-width: 450px) {
  .big-icon {
    font-size: 42px;
  }
}

.close-button-container {
  margin-top: 10px;
}

.modal-mask {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: table;
  transition: opacity 0.3s ease;
}
.modal-wrapper {
  display: table-cell;
  vertical-align: middle;
}
.modal-container {
  overflow-y: scroll;
  overflow-x: hidden;

  width: 80vw;
  /* height: 500px; */
  /* min-height: 80vw; */
  /* max-height: 100vw; */
  margin: 0px auto;
  padding: 3%;
  background-color: #fff;
  border-radius: 2px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  transition: all 0.3s ease;
  font-family: Helvetica, Arial, sans-serif;
}
.modal-header {
  margin-top: 0;
  color: #42b983;
}
.modal-body {
  margin: 20px 0;
}
.modal-default-button {
  float: right;
}
.float-right {
  float: right;
}

/*
* the following styles are auto-applied to elements with
* v-transition="modal" when their visiblity is toggled
* by Vue.js.
*
* You can easily play with the modal transition by editing
* these styles.
*/

.modal-enter,
.modal-leave {
  opacity: 0;
}
.modal-enter .modal-container,
.modal-leave .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
.center {
  text-align: center;
}

/* Transitions */
/* always present */
.expand-enter-active,
.expand-leave-active {
  padding: 1px 1px 0px 1px;
  transition: all 0.3s ease-in-out;
  overflow: hidden;
}

.expand-enter,
.expand-leave-to {
  height: 0px;
  padding: 0px 5px;
  opacity: 0;
}
</style>
